// app/javascript/controllers/selectize_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["selectize","nolieu","stickysetting", "adminmutation", "filtrecodefact"]
  static values = {
    url: { type: String, default: "/scope_selectize" },        // The URL to fetch options from
    valueField: { type: String, default: "value" }, // The field to use as the value
    labelField: { type: String, default: "label" }, // The field to use as the label
    searchField: { type: String, default: "label" }, // The field to search on
    preload: { type: Boolean, default: false },  // Whether to preload options
    createNew: { type: Boolean, default: false }, // Allow creating new items
    loadModal: { type: Boolean, default: true }, // Allow loading modal
    searchable: { type: Boolean, default: true }, // Allow searching
    allowEmptyOption: { type: Boolean, default: true }, // Allow empty option
    placeholder: { type: String, default: "Select an option..." }, // Placeholder text
    delay: { type: Number, default: 400 },       // Delay before searching
    maxOptions : {type: Number, default: 100}, // Max rendered options
    minLength: { type: Number, default: 2 }      // Minimum characters before searching
  }

  connect() {
    this.selectizeInstances = []
    this.selectizeTargets.forEach(selectizeElement => {
      this.initializeSelectize(selectizeElement)
    })
    this.nolieuTargets.forEach(element => {
      this.update_nolieu(element)
    })
    this.stickysettingTargets.forEach(element => {
      this.set_profiletablissement_and_date(element)
    })
    this.adminmutationTargets.forEach(element => {
      this.mutation_admin_user_change(element)
    })
    this.filtrecodefactTargets.forEach(element => {
      this.filtre_par_code_facturation(element)
    })    
    this.setupObserver()
    //console.log("selectize initialized!")
  }

  disconnect() {
    // Clean up all Selectize instances
    this.selectizeInstances.forEach(instance => {
      if (instance && instance.selectize) {
        instance.selectize.destroy()
      }
    })
    this.selectizeInstances = []

    if (this.observer) {
      this.observer.disconnect()
    }    
  }

  setupObserver() {
    // Create a new MutationObserver
    this.observer = new MutationObserver(mutations => {
      let needsInitialization = false
      
      // Check if any mutations added nodes to the DOM
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          needsInitialization = true
          break
        }
      }
      
      // If new nodes were added, check for new select targets
      if (needsInitialization) {
        this.selectizeTargets.forEach(selectizeElement => {
          this.initializeSelectize(selectizeElement)
        })
        this.filtrecodefactTargets.forEach(element => {
          this.filtre_par_code_facturation(element)
        })           
        //console.log("selectize reinitialized!")
      }
    })
    
    // Start observing the entire element that contains this controller
    this.observer.observe(this.element, {
      childList: true,
      subtree: true
    })
  }  

  // Initialize Selectize on a specific element
  initializeSelectize(selectizeElement) {
    // Skip if already initialized
    if (this.isElementInitialized(selectizeElement)) {
      return
    }


    // Get element-specific attributes (allowing per-element configuration)
    const url = selectizeElement.getAttribute('data-selectize-url') || this.urlValue
    const scope = selectizeElement.getAttribute('data-selectize-scope')
    const valueField = selectizeElement.getAttribute('data-selectize-value-field') || this.valueFieldValue
    const labelField = selectizeElement.getAttribute('data-selectize-label-field') || this.labelFieldValue
    const searchField = selectizeElement.getAttribute('data-selectize-search-field') || this.searchFieldValue
    const placeholder = selectizeElement.getAttribute('data-selectize-placeholder') || this.placeholderValue
    const preload = selectizeElement.hasAttribute('data-selectize-preload') ? 
      selectizeElement.getAttribute('data-selectize-preload') === 'true' : this.preloadValue
    const maxOptions = parseInt(selectizeElement.getAttribute('data-selectize-max-options') || this.maxOptionsValue)
    const minLength = parseInt(selectizeElement.getAttribute('data-selectize-min-length') || this.minLengthValue)
    const createNew = selectizeElement.hasAttribute('data-selectize-create-new') ? 
    selectizeElement.getAttribute('data-selectize-create-new') === 'true' : this.createNewValue
    const loadModal = selectizeElement.hasAttribute('data-selectize-load-modal') ? 
    selectizeElement.getAttribute('data-selectize-load-modal') === 'true' : this.loadModalValue
    const searchable = selectizeElement.hasAttribute('data-selectize-searchable') ? 
      selectizeElement.getAttribute('data-selectize-searchable') === 'true' : this.searchableValue
    const allowEmptyOption = selectizeElement.hasAttribute('data-selectize-allow-empty-option') ? 
      selectizeElement.getAttribute('data-selectize-allow-empty-option') === 'true' : this.allowEmptyOptionValue
    const object_name = selectizeElement.getAttribute('data-selectize-object-name')
    const object_reference = selectizeElement.getAttribute('data-selectize-object-reference')
    const $element = $(selectizeElement)

    // Store element-specific configuration
    const config = {
      element: selectizeElement,
      url: url,
      scope: scope,
      valueField: valueField,
      labelField: labelField,
      searchField: searchField.split(','),
      searchParam: "q",
      sourceParam: "scope_selectize",
      placeholder: placeholder,
      preload: preload,
      minLength: minLength,
      maxOptions: maxOptions,
      createNew: createNew,
      loadModal: loadModal,
      searchable: searchable,
      allowEmptyOption: allowEmptyOption,
      object_name: object_name,
      object_reference: object_reference,
      selectizeCallback: null
    }

    // Add modal if createNew function is enabled
    if (config.createNew && config.object_name && config.loadModal){
      this.modalTriggers(config);
    }    

    const selectizeConfig = {
      valueField: config.valueField,
      labelField: config.labelField,
      searchField: config.searchField,
      placeholder: config.placeholder,
      plugins: config.allowEmptyOption ? ['remove_button'] : [],
      dropdownParent: 'body',
      load: config.searchable ? (query, callback) => this.loadOptions(query, callback, config) : false,
      onType: config.searchable ? (input) => this.handleTyping(input, config) : false,
      render: {
        option_create: (item, escape) => this.renderOptionCreate(item, escape, config),
        option: (item, escape) => this.renderOption(item, escape, config),
        item: (item, escape) => this.renderItem(item, escape, config)
      },
      preload: config.preload,
      persist: false,
      loadThrottle: config.delay,
      searchable: config.searchable,
      allowEmptyOption: config.allowEmptyOption,  
      create: config.createNew ? (input, callback) => this.createItem(input, callback, config) : false,
      createFilter: config.createNew ? (input) => this.createFilter(input) : false,
      maxOptions: config.maxOptions
    }

    // Get the Rails CSRF token
    const csrfToken = document

    // Initialize Selectize
    const selectizeInstance = $element.selectize(selectizeConfig)[0].selectize

    // Intercepter le copier-coller dans le champ de saisie de Selectize
    selectizeInstance.$control_input.on('paste', (e) => {
      // Récupérer le texte provenant du presse-papiers
      const pastedData = (e.originalEvent || e).clipboardData.getData('text');
      
      setTimeout(() => {
        // Mettre à jour la valeur et forcer l'éveil de la recherche
        selectizeInstance.setTextboxValue(pastedData);
        selectizeInstance.onKeyUp(); 
      }, 10);
    });

    // Store the instance and configuration for later reference
    this.selectizeInstances.push({
      element: selectizeElement,
      selectize: selectizeInstance,
      config: config
    })

    // Add CSRF token to all AJAX requests if not already set
    if (csrfToken && !$.ajaxSettings.headers) {
      $.ajaxSetup({
        beforeSend: function(xhr) {
          xhr.setRequestHeader('X-CSRF-Token', csrfToken)
        }
      })
    }

    return selectizeInstance
  }

  isElementInitialized(element) {
    // Check if the element is already in the instances array
    return this.selectizeInstances.some(instance => 
      instance.element === element
    )
  }  

  // Load options via AJAX
  loadOptions(query, callback, config) {
    if (!query.length && !config.preload) {
      return callback()
    }

    if (query.length < config.minLength && !config.preload) {
      return callback()
    }

    if (!config.url || !config.scope) {
      return callback()
    }

    const url = new URL(config.url, window.location.origin)
    const params = new URLSearchParams(url.search)
    params.set(config.searchParam, query)
    params.set(config.sourceParam, config.scope)
    url.search = params.toString()

    fetch(url)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok')
        }
        return response.json()
      })
      .then(data => {
        callback(data)
      })
      .catch(error => {
        console.error('Error loading options:', error)
        callback()
      })
  }

  // Create a new item
  createItem(input, callback, config) {
    if (!input.length) return callback()
    if (!config.object_name) {
      console.error("No Object Name provided for Selectize")
      return callback()
    }

    const auto_create_objects = (input, callback, config) => {
      if( !config.object_name.includes("personne") && !config.object_name.includes("etablissement") ){
        return false;
      }   
      // Define regex patterns
      input = input.replace(/\s+/g, '');
      const patterns = {
        NAM: /^([a-z]{4}\d{2}[0-1,5-6]\d[0-3]\d{3})$/i,
        Resident: /^5\d{5}$/i,
        Externe: /^E\d{8}$/i,
        BB: /^([M|F]20\d{2}[0|1]\d[0,1,2,3]\d<[a-z]{4}\d{2}[0-1,5-6]\d[0-3]\d{3})$/i,
        Etablissement: /^([A-z]{1}\d{1}[A-z]{1}\d{1}[A-z]{1}\d{1})$/i
      };
    
      // Helper function to handle AJAX requests
      const createEntity = (url, data, callback, entityType = 'patient') => {
        $.ajax({
          method: "POST",
          url,
          data,
          dataType: "json",
          error: function(response) {
            alert(`Erreur à la création ${entityType === 'patient' ? 'du' : 'de l\''} ${entityType}...`);
            callback();
            callback = null;
          },
          success: function(response) {
            if (response["errors"]) {
              alert(response["errors"]);
              callback();
            } else {
              callback(response);
            }
            
            if (url !== '/personne_patients' || entityType !== 'enfant') {
              callback = null;
            }
          }
        });
      };
    
      // Format the callback data based on object_name
      const formatResponse = (response) => {
        return {
          value: config.object_name == "personne_patient" ? response.id : response.signature,
          label: response.conversion_affichage
        };
      };
    
      // Handle baby patient (BB)
      if (input.match(patterns.BB)) {
        const nam_repondant = input.split('<')[1];
        const patient_bb = { 
          authenticity_token: AUTH_TOKEN,
          personne_patient: {
            prenom: "BB",
            nom: nam_repondant.slice(0, 4),
            code_sexe: input.slice(0, 1) === "M" ? "1" : "2",
            "date_naissance(d)": `${input.slice(1, 5)}-${input.slice(5, 7)}-${input.slice(7, 9)}`
          }
        };
    
        createEntity('/personne_patients', patient_bb, (response) => {
          callback(formatResponse(response));
          
          // Create the Repondant patient
          const repondant = { 
            authenticity_token: AUTH_TOKEN, 
            personne_patient: { nam: nam_repondant }
          };
          
          createEntity('/personne_patients', repondant, (response) => {
            // Set the repondant in the form
            const repondantSelectize = $(`[id^=facture_personne_repondant_id-${config.element.id}]`)[0].selectize;
            repondantSelectize.clearOptions();
            repondantSelectize.addOption([{label: response.conversion_affichage, value: response.id}]);
            repondantSelectize.setValue(response.id);
            
            // Set situation for < 1 year
            const situationSelectize = $(`[id^=facture_personne_situation-${config.element.id}]`)[0].selectize;
            situationSelectize.setValue(2);
          }, 'répondant');
        }, 'enfant');
        
        return true;
      } 
      // Handle NAM patient
      else if (input.match(patterns.NAM)) {
        const patient = { 
          authenticity_token: AUTH_TOKEN, 
          personne_patient: { nam: input }
        };
        
        createEntity('/personne_patients', patient, (response) => {
          callback(formatResponse(response));
        });
        
        return true;
      } 
      // Handle Resident or Externe
      else if (input.match(patterns.Resident) || input.match(patterns.Externe)) {
        const type_personne = input.match(patterns.Resident) ? "7" : "4";
        const personne_autre = { 
          authenticity_token: AUTH_TOKEN, 
          personne_autre: {
            type_identifiant: "4", 
            type_personne, 
            identifiant: input
          }
        };
        
        createEntity('/personne_autres', personne_autre, (response) => {
          callback({value: response.signature, label: response.conversion_affichage});
        }, 'personne_autre');
        
        return true;
      }// Handle Etablissement
      else if (input.match(patterns.Etablissement)) {
        const etablissement = { 
          authenticity_token: AUTH_TOKEN, 
          etablissement_geographique: { code_postal: input.replace(/\s+/g, '').toUpperCase(),
                                        nom: "DEPL_" + input.replace(/\s+/g, '').toUpperCase(),
                                        type_lieu: "D"}
        };
        
        createEntity('/etablissement_geographiques', etablissement, (response) => {
          callback(formatResponse(response));
        }, 'etablissement_geographique');
        
        return true;
      } 
      
      return false;
    }
    
    //auto create object or load modal
    if( !(auto_create_objects(input, callback, config)) && config.loadModal){
      const render_modal = new bootstrap.Modal(document.getElementById(config.object_name + '-modal'))
      render_modal.show()
      //set value from input if ref fields
      document.getElementById(config.object_reference).value = input;
      return
    }
  }

  createFilter(input) {
    var match, regex;
    //Check for NAM|Resident|Externe
    regex = /^((([a-z]{4}\d{2}[0-1,5-6]\d[0-3]\d{3})$|^([M|F]20\d{2}[0|1]\d[0,1,2,3]\d<[a-z]{4}\d{2}[0-1,5-6]\d[0-3]\d{3})))$|(^5\d{5}$)|(^E\d{8}$)|(^([A-z]{1}\d{1}[A-z]{1}\d{1}[A-z]{1}\d{1})$)/i;
    match = input.replace(/\s+/g, '').match(regex);

    if (match) return true;
    return false;
  }

  // Handle typing events
  handleTyping(input, config) {
    // Add spaces every four characters for personnes and patients
    let formattedInput = input;
    if(config.object_name && 
      //(config.object_name.includes("personnes") || config.object_name.includes("patients"))){
      (config.object_name.includes("personne"))){
      formattedInput = this.formatWithSpaces(input);
      if (formattedInput !== input) {
        const selectizeControl = config.element.selectize;
        selectizeControl.setTextboxValue(formattedInput);
      }
    }
    
    if (formattedInput.length >= config.minLength) {
      const event = new CustomEvent('selectize:typing', { 
        detail: { query: formattedInput, element: config.element }
      });
      config.element.dispatchEvent(event);
    }
  }
  
  // Helper function to add spaces every 4 characters
  formatWithSpaces(input) {
    // Remove existing spaces first
    const withoutSpaces = input.replace(/\s/g, '');
    
    // Add a space after every 4 characters
    return withoutSpaces.replace(/(.{4})(?=.)/g, '$1 ').trim();
  }

  //Render a create option in the dropdown
  renderOptionCreate(item, escape, config) {
    if (config.createNew){
      return '<div class="create">Ajouter > <strong>' + escape(item.input) + '</strong>&hellip;</div>'; 
    }
  }

  // Render an option in the dropdown
  renderOption(item, escape, config) {
    return `<div class="option">
      <span class="name">${escape(item[config.labelField])}</span>
    </div>`
  }

  // Render a selected item
  renderItem(item, escape, config) {
    return `<div class="item">
      <span class="name">${escape(item[config.labelField])}</span>
    </div>`
  }

  modalTriggers(config) {
    //Handler a la fermeture du modal, nettoyage
    $("." + config.object_name + "-modal").on("hide.bs.modal", function(e){
      if(config.selectizeCallback != null){
        config.selectizeCallback();
      }
    });

    //Handler pour un submit de la personne a partir du modal
    $("#new_modal_"+ config.object_name).on("submit", function(e){
      // Override le submit usuel et pousse en AJAX la creation de la personne
      e.preventDefault();
      $.ajax({
        method: "POST",
        url: '/' + config.object_name + 's',
        data: $(this).serialize(),
        dataType: "json",
        error: function(response) {
          alert("Erreur lors de la création...");
          config.selectizeCallback();
          config.selectizeCallback = null;
          const submitButton = document.querySelector(`#${config.object_name}_submit`);
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('disabled');
          }          
        },
        success: function(response){
          // Affiche les erreurs de validation
          if(response["errors"]){
            alert(response["errors"]);
          }
          else{
            config.selectizeCallback({value: response.id, label: response.conversion_affichage})
            document.activeElement.blur();
            $("." + config.object_name + "-modal").modal('toggle');
          }
          // Reinitialise le modal pour la prochaine creation
          config.selectizeCallback = null;
          const submitButton = document.querySelector(`#${config.object_name}_submit`);
          if (submitButton) {
            submitButton.disabled = false;
            submitButton.classList.remove('disabled');
          }  
        }
      });
    });
  }

  update_nolieu(element) {
    element.selectize.on("change", function(e) {
      const element_value = this.getValue()
      const element_id = this.$input[0].id
      // Generate the IDs for related fields by replacing the string pattern
      const liste_no_id = element_id.replace(/etablissement(?!.*?etablissement)/, "liste_no");
      const secteur_activite_id = element_id.replace(/etablissement(?!.*?etablissement)/, "secteur_activite_id");
      const no_lieu_id = element_id.replace(/etablissement(?!.*?etablissement)/, "no_lieu");
      // Create and send the fetch request
      $.ajax({
        url: '/filtre_par_etablissement',
        type: 'GET',
        //dataType: "script",
        data: { etablissement: element_value,
                liste_no_id: liste_no_id,
                no_lieu_id: no_lieu_id,
                secteur_activite_id: secteur_activite_id
              }
      });
    });
  }

  set_profiletablissement_and_date(element){
    element.selectize.on("change", function(e) {
      const datePicker = document.querySelector('input.date_picker');
      let dateValue = '';
      if (datePicker) {dateValue = $(datePicker).datepicker('getDate')}
      let searchParams  = new URLSearchParams(window.location.search);
      searchParams.delete('profil_etablissement_id');
      searchParams.delete('date_service_en_cours');
      window.location.search = searchParams.toString() + '&profil_etablissement_id=' + this.getValue() + '&date_service_en_cours=' + dateValue;
    });
  }
  
  mutation_admin_user_change(element){
    element.selectize.on("change", function(e) {
      const utilisateur_id = this.getValue()
      $.ajax({
        url: '/mutation_admin_user_change',
        type: 'GET',
        data: { utilisateur_id: utilisateur_id }
      });
    });
  }

  filtre_par_code_facturation(element){
    element.selectize.on("change", function(e) {
      const element_value = this.getValue()
      const element_id = this.$input[0].id
      if (element_value){
        const action_descriptor = $("form[class*='facture']").first().attr('id')

        //Construction des ID des champs a modifier selon # de lignes simpleform
        const field_role_id = element_id.replace('facturation_code_id','code_role');
        const field_contexte_id = element_id.replace('facturation_code_id','elements_contexte');
        const field_mesure_id = element_id.replace('facturation_code_id','elements_mesure');

        $.ajax({
          url: '/filtre_par_code_facturation',
          type: 'GET',
          data: { action_descriptor: action_descriptor,
                  field_role_id: field_role_id,
                  field_contexte_id: field_contexte_id,
                  field_mesure_id: field_mesure_id,
                  facturation_code_id: element_value }
        });
      }
    });
  }

  // === Public API methods - can be called from other controllers ===

  // Clear the selected items for a specific select element
  clear(element) {
    const instance = this.findSelectizeInstance(element)
    if (instance && instance.selectize) {
      instance.selectize.clear()
    }
  }

  // Refresh options for a specific select element
  refresh(element) {
    const instance = this.findSelectizeInstance(element)
    if (instance && instance.selectize) {
      instance.selectize.clearOptions()
      if (instance.config.preload) {
        instance.selectize.load(query => this.loadOptions('', query, instance.config))
      }
    }
  }

  // Add a new option programmatically to a specific select element
  addOption(element, option) {
    const instance = this.findSelectizeInstance(element)
    if (instance && instance.selectize && option) {
      instance.selectize.addOption(option)
      instance.selectize.refreshOptions(false)
    }
  }

  // Set a value programmatically for a specific select element
  setValue(element, value) {
    const instance = this.findSelectizeInstance(element)
    if (instance && instance.selectize) {
      instance.selectize.setValue(value, true)
    }
  }

  // Helper to find the Selectize instance for a specific element
  findSelectizeInstance(element) {
    if (!this.selectInstances) return null
    
    // If element is a DOM element, find by element reference
    if (element instanceof HTMLElement) {
      return this.selectInstances.find(instance => instance.element === element)
    }
    
    // If element is a string (selector or ID), find by ID or query selector
    if (typeof element === 'string') {
      if (element.startsWith('#')) {
        return this.selectInstances.find(instance => instance.element.id === element.substring(1))
      } else {
        const domElement = document.querySelector(element)
        return this.selectInstances.find(instance => instance.element === domElement)
      }
    }
    
    // If element is a number (index), return by index
    if (typeof element === 'number' && element >= 0 && element < this.selectInstances.length) {
      return this.selectInstances[element]
    }
    
    return null
  }
}